// src/pages/OffresEdit.js - VERSION AVEC QUOTAS
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const OffresEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    libele_offre: "",
    description: "",
    duree_mois: "",
    type_offre: "",
    montant: "",
    max_clients: "",
    max_sites: "",
    max_tags_qr: "",
    max_tags_nfc: "",
    type_tags: "both"
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [quotaOptions, setQuotaOptions] = useState({
    clients: true,
    sites: true,
    tags_qr: true,
    tags_nfc: true
  });

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Types d'offres disponibles
  const TYPE_OPTIONS = [
    { value: '', label: 'Sélectionner un type' },
    { value: 'mensuel', label: 'Mensuel' },
    { value: 'trimestriel', label: 'Trimestriel' },
    { value: 'semestriel', label: 'Semestriel' },
    { value: 'annuel', label: 'Annuel' }
  ];

  // Types de tags disponibles
  const TAG_TYPE_OPTIONS = [
    { value: 'both', label: 'QR Code + NFC' },
    { value: 'qr', label: 'QR Code uniquement' },
    { value: 'nfc', label: 'NFC uniquement' }
  ];

  // Suggestions de durée par type
  const DURATION_SUGGESTIONS = {
    mensuel: [1],
    trimestriel: [3],
    semestriel: [6],
    annuel: [12, 24, 36]
  };

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (id) {
      if (!id || isNaN(parseInt(id))) {
        setServerError("ID d'offre invalide dans l'URL");
        setTimeout(() => navigate("/offres"), 3000);
      } else {
        fetchOffre();
      }
    }
  }, [id]);

  // Mettre à jour les champs de tags selon le type sélectionné
  useEffect(() => {
    if (form.type_tags === 'qr') {
      setQuotaOptions(prev => ({ ...prev, tags_qr: true, tags_nfc: false }));
      setForm(prev => ({ ...prev, max_tags_nfc: "0" }));
    } else if (form.type_tags === 'nfc') {
      setQuotaOptions(prev => ({ ...prev, tags_qr: false, tags_nfc: true }));
      setForm(prev => ({ ...prev, max_tags_qr: "0" }));
    } else {
      setQuotaOptions(prev => ({ ...prev, tags_qr: true, tags_nfc: true }));
    }
  }, [form.type_tags]);

  // Récupérer les données de l'offre
  const fetchOffre = async () => {
    setLoadingData(true);
    setServerError("");
    
    if (!id || isNaN(parseInt(id))) {
      setServerError("ID d'offre invalide");
      setLoadingData(false);
      setTimeout(() => navigate("/offres"), 2000);
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      setLoadingData(false);
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    try {
      const url = `${API_BASE_URL}/offres/get.php?id=${id}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      if (response.data?.success && response.data?.offre) {
        const offreData = response.data.offre;
        
        setForm({
          libele_offre: offreData.libele_offre || "",
          description: offreData.description || "",
          duree_mois: offreData.duree_mois ? offreData.duree_mois.toString() : "",
          type_offre: offreData.type_offre || "",
          montant: offreData.montant ? parseFloat(offreData.montant).toString() : "",
          max_clients: offreData.max_clients ? offreData.max_clients.toString() : "0",
          max_sites: offreData.max_sites ? offreData.max_sites.toString() : "0",
          max_tags_qr: offreData.max_tags_qr ? offreData.max_tags_qr.toString() : "0",
          max_tags_nfc: offreData.max_tags_nfc ? offreData.max_tags_nfc.toString() : "0",
          type_tags: offreData.type_tags || "both"
        });
        
      } else {
        throw new Error(response.data?.message || "Données manquantes dans la réponse");
      }
    } catch (error) {
      console.error("Erreur:", error);
      
      let userMessage = "Erreur lors du chargement de l'offre";
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch(status) {
          case 400:
            userMessage = "ID d'offre invalide";
            break;
          case 401:
            userMessage = "Session expirée. Veuillez vous reconnecter.";
            localStorage.clear();
            setTimeout(() => navigate("/login"), 2000);
            break;
          case 403:
            userMessage = "Accès non autorisé";
            break;
          case 404:
            userMessage = `Offre #${id} non trouvée dans la base de données`;
            setTimeout(() => navigate("/offres"), 3000);
            break;
          case 500:
            userMessage = "Erreur serveur. Veuillez contacter l'administrateur.";
            break;
          default:
            userMessage = data?.message || `Erreur ${status}`;
        }
      } else if (error.request) {
        userMessage = "Impossible de joindre le serveur. Vérifiez votre connexion.";
      } else {
        userMessage = error.message;
      }
      
      setServerError(userMessage);
    } finally {
      setLoadingData(false);
    }
  };

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    // Suggestions automatiques de durée selon le type
    if (name === "type_offre" && value && DURATION_SUGGESTIONS[value]) {
      setForm(prev => ({ 
        ...prev, 
        duree_mois: DURATION_SUGGESTIONS[value][0].toString() 
      }));
    }
  };

  // Validation des nombres
  const validateNumber = (value, fieldName, min, max) => {
    if (value === "" || value === null) return `Le ${fieldName} est requis`;
    const num = parseFloat(value);
    if (isNaN(num)) return `Le ${fieldName} doit être un nombre valide`;
    if (min !== undefined && num < min) return `Le ${fieldName} doit être au moins ${min}`;
    if (max !== undefined && num > max) return `Le ${fieldName} ne doit pas dépasser ${max}`;
    return null;
  };

  // Validation du formulaire
  const validate = () => {
    const e = {};
    
    // Validation libellé
    if (!form.libele_offre.trim()) {
      e.libele_offre = "Le libellé de l'offre est requis.";
    } else if (form.libele_offre.length < 3) {
      e.libele_offre = "Le libellé doit contenir au moins 3 caractères.";
    }
    
    // Validation durée
    const dureeError = validateNumber(form.duree_mois, 'durée', 1, 120);
    if (dureeError) e.duree_mois = dureeError;
    
    // Validation type
    if (!form.type_offre) {
      e.type_offre = "Le type d'offre est requis.";
    }
    
    // Validation montant
    const montantError = validateNumber(form.montant, 'montant', 100, 10000000);
    if (montantError) e.montant = montantError;

    // Validation quotas
    const maxClientsError = validateNumber(form.max_clients, 'nombre max de clients', 0, 999999);
    if (maxClientsError) e.max_clients = maxClientsError;

    const maxSitesError = validateNumber(form.max_sites, 'nombre max de sites', 0, 999999);
    if (maxSitesError) e.max_sites = maxSitesError;

    if (quotaOptions.tags_qr) {
      const maxTagsQrError = validateNumber(form.max_tags_qr, 'nombre max de tags QR', 0, 999999);
      if (maxTagsQrError) e.max_tags_qr = maxTagsQrError;
    }

    if (quotaOptions.tags_nfc) {
      const maxTagsNfcError = validateNumber(form.max_tags_nfc, 'nombre max de tags NFC', 0, 999999);
      if (maxTagsNfcError) e.max_tags_nfc = maxTagsNfcError;
    }

    if (!form.type_tags) {
      e.type_tags = "Le type de tags est requis.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMsg("");
    
    if (!validate()) return;
    
    setLoading(true);

    try {
      const url = `${API_BASE_URL}/offres/update.php`;
      const token = localStorage.getItem("access_token");

      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const dataToSend = {
        id_offre: parseInt(id),
        libele_offre: form.libele_offre,
        description: form.description,
        duree_mois: parseInt(form.duree_mois),
        type_offre: form.type_offre,
        montant: parseFloat(form.montant),
        max_clients: parseInt(form.max_clients),
        max_sites: parseInt(form.max_sites),
        max_tags_qr: parseInt(form.max_tags_qr),
        max_tags_nfc: parseInt(form.max_tags_nfc),
        type_tags: form.type_tags
      };

      const response = await axios.post(url, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;
      
      if (data.success) {
        setSuccessMsg("✅ Offre mise à jour avec succès. Redirection vers la liste des offres...");
        
        // Rediriger vers la liste des offres après 2 secondes
        setTimeout(() => {
          navigate("/offres");
        }, 2000);
        
      } else {
        setServerError(data.message || "Erreur lors de la mise à jour.");
      } 
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response) {
        const { status, data } = err.response;
        
        if (status === 401) {
          setServerError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
        } else if (status === 403) {
          setServerError(data.message || "Permission refusée. Réservé aux administrateurs.");
        } else if (status === 404) {
          setServerError("Offre non trouvée.");
        } else if (status === 400) {
          if (data.message) {
            const errorMsg = data.message.toLowerCase();
            const fieldErrors = {};
            
            if (errorMsg.includes("libele")) fieldErrors.libele_offre = data.message;
            else if (errorMsg.includes("duree")) fieldErrors.duree_mois = data.message;
            else if (errorMsg.includes("type") && !errorMsg.includes("tags")) fieldErrors.type_offre = data.message;
            else if (errorMsg.includes("montant")) fieldErrors.montant = data.message;
            else if (errorMsg.includes("client")) fieldErrors.max_clients = data.message;
            else if (errorMsg.includes("site")) fieldErrors.max_sites = data.message;
            else if (errorMsg.includes("qr")) fieldErrors.max_tags_qr = data.message;
            else if (errorMsg.includes("nfc")) fieldErrors.max_tags_nfc = data.message;
            else if (errorMsg.includes("tag")) fieldErrors.type_tags = data.message;
            
            if (Object.keys(fieldErrors).length > 0) {
              setErrors(fieldErrors);
            } else {
              setServerError(data.message);
            }
          } else {
            setServerError("Veuillez vérifier les informations saisies.");
          }
        } else {
          setServerError(data?.message || `Erreur ${status}: Impossible de mettre à jour l'offre.`);
        }
      } else if (err.request) {
        setServerError("Serveur inaccessible. Vérifiez votre connexion.");
      } else {
        setServerError("Erreur: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur a les permissions
  if (currentProfile !== "1") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une offre</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Tableau de bord
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/offres" onClick={(e) => { e.preventDefault(); navigate("/offres"); }}>
                  Offres
                </a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-lock fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                Vous n'avez pas les permissions nécessaires pour modifier des offres. Cette fonctionnalité est réservée aux administrateurs.
              </p>
              
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/offres")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux offres
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (loadingData) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une offre</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Tableau de bord
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/offres" onClick={(e) => { e.preventDefault(); navigate("/offres"); }}>
                  Offres
                </a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données de l'offre...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier une offre</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                Tableau de bord
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href="/offres" onClick={(e) => { e.preventDefault(); navigate("/offres"); }}>
                Offres
              </a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-pencil-square"></i>
                    </div>
                    Modifier une offre
                  </div>
                </h5>
                
                {serverError && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{serverError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setServerError("")}
                      ></button>
                    </div>
                  </div>
                )}
                
                {successMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">{successMsg}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMsg("")}
                      ></button>
                    </div>
                  </div>
                )}

                <form className="row g-3" onSubmit={handleSubmit}>
                  {/* Bloc : Informations générales */}
                  <div className="col-12">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-info-circle"></i>
                      </div>
                      <h6 style={{ color: COLORS.primary, fontWeight: '600', marginBottom: '0' }}>
                        Informations générales
                      </h6>
                    </div>
                  </div>

                  {/* Ligne avec 4 champs alignés */}
                  <div className="col-md-3">
                    <label htmlFor="libele_offre" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-tag me-1" style={{ color: COLORS.secondary }}></i>
                      Libellé <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.libele_offre ? COLORS.danger : '#e0e0e0',
                        color: errors.libele_offre ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-card-heading"></i>
                      </span>
                      <input
                        type="text"
                        id="libele_offre"
                        name="libele_offre"
                        className={`form-control ${errors.libele_offre ? "is-invalid" : ""}`}
                        value={form.libele_offre}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: Premium"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.libele_offre ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                    {errors.libele_offre && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.libele_offre}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="type_offre" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-calendar me-1" style={{ color: COLORS.secondary }}></i>
                      Type <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.type_offre ? COLORS.danger : '#e0e0e0',
                        color: errors.type_offre ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-calendar-event"></i>
                      </span>
                      <select
                        id="type_offre"
                        name="type_offre"
                        className={`form-control ${errors.type_offre ? "is-invalid" : ""}`}
                        value={form.type_offre}
                        onChange={handleChange}
                        disabled={loading}
                        required
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.type_offre ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      >
                        {TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.type_offre && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.type_offre}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="duree_mois" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-clock me-1" style={{ color: COLORS.secondary }}></i>
                      Durée (mois) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.duree_mois ? COLORS.danger : '#e0e0e0',
                        color: errors.duree_mois ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-hourglass"></i>
                      </span>
                      <input
                        type="number"
                        id="duree_mois"
                        name="duree_mois"
                        className={`form-control ${errors.duree_mois ? "is-invalid" : ""}`}
                        value={form.duree_mois}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: 12"
                        min="1"
                        max="120"
                        step="1"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.duree_mois ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                    {errors.duree_mois && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.duree_mois}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="montant" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-cash me-1" style={{ color: COLORS.secondary }}></i>
                      Montant (FCFA) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.montant ? COLORS.danger : '#e0e0e0',
                        color: errors.montant ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-cash-stack"></i>
                      </span>
                      <input
                        type="number"
                        id="montant"
                        name="montant"
                        className={`form-control ${errors.montant ? "is-invalid" : ""}`}
                        value={form.montant}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: 12000"
                        min="100"
                        max="10000000"
                        step="100"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.montant ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                    {errors.montant && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.montant}</div>
                    )}
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="description" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-text-paragraph me-1" style={{ color: COLORS.secondary }}></i>
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control"
                      value={form.description}
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="Décrivez les avantages, fonctionnalités, conditions..."
                      rows="3"
                      style={{ 
                        borderColor: '#e0e0e0',
                        borderRadius: '8px',
                        color: COLORS.darkText,
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Bloc : Quotas */}
                  <div className="col-12 mt-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-pie-chart"></i>
                      </div>
                      <h6 style={{ color: COLORS.primary, fontWeight: '600', marginBottom: '0' }}>
                        Quotas inclus
                      </h6>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="max_clients" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-people me-1" style={{ color: COLORS.secondary }}></i>
                      Clients max <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.max_clients ? COLORS.danger : '#e0e0e0',
                        color: errors.max_clients ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-person-badge"></i>
                      </span>
                      <input
                        type="number"
                        id="max_clients"
                        name="max_clients"
                        className={`form-control ${errors.max_clients ? "is-invalid" : ""}`}
                        value={form.max_clients}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: 50"
                        min="0"
                        step="1"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.max_clients ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                    {errors.max_clients && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.max_clients}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="max_sites" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-building me-1" style={{ color: COLORS.secondary }}></i>
                      Sites max <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.max_sites ? COLORS.danger : '#e0e0e0',
                        color: errors.max_sites ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-geo-alt"></i>
                      </span>
                      <input
                        type="number"
                        id="max_sites"
                        name="max_sites"
                        className={`form-control ${errors.max_sites ? "is-invalid" : ""}`}
                        value={form.max_sites}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: 20"
                        min="0"
                        step="1"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.max_sites ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                    {errors.max_sites && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.max_sites}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="type_tags" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <i className="bi bi-qr-code me-1" style={{ color: COLORS.secondary }}></i>
                      Type de tags <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: errors.type_tags ? COLORS.danger : '#e0e0e0',
                        color: errors.type_tags ? COLORS.danger : COLORS.secondary
                      }}>
                        <i className="bi bi-tags"></i>
                      </span>
                      <select
                        id="type_tags"
                        name="type_tags"
                        className={`form-control ${errors.type_tags ? "is-invalid" : ""}`}
                        value={form.type_tags}
                        onChange={handleChange}
                        disabled={loading}
                        required
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.type_tags ? COLORS.danger : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: COLORS.darkText
                        }}
                      >
                        {TAG_TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.type_tags && (
                      <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.type_tags}</div>
                    )}
                  </div>

                  {quotaOptions.tags_qr && (
                    <div className="col-md-6">
                      <label htmlFor="max_tags_qr" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                        <i className="bi bi-qr-code me-1" style={{ color: COLORS.secondary }}></i>
                        Tags QR max <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: COLORS.lightBg,
                          borderColor: errors.max_tags_qr ? COLORS.danger : '#e0e0e0',
                          color: errors.max_tags_qr ? COLORS.danger : COLORS.secondary
                        }}>
                          <i className="bi bi-qr-code-scan"></i>
                        </span>
                        <input
                          type="number"
                          id="max_tags_qr"
                          name="max_tags_qr"
                          className={`form-control ${errors.max_tags_qr ? "is-invalid" : ""}`}
                          value={form.max_tags_qr}
                          onChange={handleChange}
                          disabled={loading}
                          placeholder="Ex: 100"
                          min="0"
                          step="1"
                          autoComplete="off"
                          style={{ 
                            borderColor: errors.max_tags_qr ? COLORS.danger : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: COLORS.darkText
                          }}
                        />
                      </div>
                      {errors.max_tags_qr && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.max_tags_qr}</div>
                      )}
                    </div>
                  )}

                  {quotaOptions.tags_nfc && (
                    <div className="col-md-6">
                      <label htmlFor="max_tags_nfc" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                        <i className="bi bi-wifi me-1" style={{ color: COLORS.secondary }}></i>
                        Tags NFC max <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: COLORS.lightBg,
                          borderColor: errors.max_tags_nfc ? COLORS.danger : '#e0e0e0',
                          color: errors.max_tags_nfc ? COLORS.danger : COLORS.secondary
                        }}>
                          <i className="bi bi-wifi"></i>
                        </span>
                        <input
                          type="number"
                          id="max_tags_nfc"
                          name="max_tags_nfc"
                          className={`form-control ${errors.max_tags_nfc ? "is-invalid" : ""}`}
                          value={form.max_tags_nfc}
                          onChange={handleChange}
                          disabled={loading}
                          placeholder="Ex: 50"
                          min="0"
                          step="1"
                          autoComplete="off"
                          style={{ 
                            borderColor: errors.max_tags_nfc ? COLORS.danger : '#e0e0e0',
                            borderRadius: '0 8px 8px 0',
                            color: COLORS.darkText
                          }}
                        />
                      </div>
                      {errors.max_tags_nfc && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.max_tags_nfc}</div>
                      )}
                    </div>
                  )}

                  {/* Boutons */}
                  <div className="col-12 d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                    <div className="d-flex gap-3">
                      <button 
                        type="submit" 
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: COLORS.white,
                          backgroundColor: loading ? '#B0BEC5' : COLORS.primary,
                          borderColor: loading ? '#B0BEC5' : COLORS.primary,
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          opacity: loading ? 0.8 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.borderColor = COLORS.primary;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Mise à jour en cours...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={() => navigate("/offres")}
                        disabled={loading}
                        style={{
                          color: COLORS.darkText,
                          borderColor: '#e0e0e0',
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '12px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.lightBg;
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#e0e0e0';
                        }}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Annuler
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .alert,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          opacity: 1;
          height: auto;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between,
          .d-flex.gap-3 {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div,
          .d-flex.gap-3 {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .row .col-md-3,
          .row .col-md-4,
          .row .col-md-6 {
            margin-bottom: 16px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default OffresEdit;